import { imageDimensionsFromStream } from "image-dimensions";
import { showDirectoryPicker, showOpenFilePicker } from "file-system-access";

interface ImageInfo {
  path: string;
  size: [number, number, number];
}

interface Album {
  id: string;
  name: string;
  count: number;
  images: ImageInfo[];
}

interface PngDimensions {
  width: number;
  height: number;
  type: "png";
  orientation?: number;
}

interface GifDimensions {
  width: number;
  height: number;
  type: "gif";
  orientation?: number;
}

interface WebpDimensions {
  width: number;
  height: number;
  type: "webp";
  orientation?: number;
}

interface JpegDimensions {
  width: number;
  height: number;
  type: "jpeg";
  orientation: number | null;
}

interface ResizeImageArg {
  in_filename: string;
  angle: number;
  crop: {
    top: number;
    left: number;
    width: number;
    height: number;
  };
  out_size: {
    width: number;
    height: number;
  };
}

type ImageDimensions =
  | PngDimensions
  | GifDimensions
  | WebpDimensions
  | JpegDimensions;

let dropIsActive = false;
const allImages: File[] = [];
const allAlbums: Album[] = [];
let allTestingImages: Array<{
  file: string;
  width: number;
  height: number;
}> = [];
const testingRootPath = "testing/";

// Augment global window interface
declare global {
  interface Window {
    exifr: {
      parse(file: File, options: any): Promise<any>;
    };
    pma_js_image_dimensions(
      filename: string
    ): Promise<[number, number, number]>;
    pma_js_pick_images(): Promise<void>;
    pma_js_get_images(): string[];
    pma_js_get_albums(): Album[];
    pma_js_get_image_data(photoId: string): Promise<Uint8Array>;
    pma_js_resize_image(jsonArg: string): Promise<string>;
    pma_js_resized_image_bytes(tmpKey: string): Uint8Array;
    pma_js_add_folder(): Promise<string[]>;
    pma_js_add_files(): Promise<string[]>;
    pma_js_set_drop_active(newValue: boolean): void;
    pma_js_p_tmp_images: { key: string; bytes: Uint8Array }[];
    pma_js_p_pick_images_has_run: boolean;
    pma_js_get_default_language(): string;
    pmaFlSmartphoneSelectionServiceUpdateAlbums(albums: Album[]): void;
    pmaFlPersonalizePageAddPhotos(photos: string[]): void;
    pmaFlPersonalizePageSetHovering(isHovering: boolean): void;
  }
}

window.pma_js_image_dimensions = async (filename: string) => {
  if ((window as any).pma_js_testing === true) {
    const image = allTestingImages.find(
      (iter) => testingRootPath + iter.file === filename
    );
    if (image === undefined) {
      throw new Error(`Couldn't get dimensions for TESTING image ${filename}`);
    }
    return [image.width, image.height, 0];
  }
  const file = allImages.find(
    (it) => it.webkitRelativePath + it.name === filename
  );
  if (!file) {
    throw new Error(`Couldn't get dimensions for image ${filename}`);
  }

  const bitmap = await createImageBitmap(file);
  return [bitmap.width, bitmap.height, 0];

  /*
             const blob = file.slice(0, 16384);
             const buffer = new Uint8Array(await blob.arrayBuffer());

             let dimensions: ImageDimensions;

             if (isPng(buffer)) {
             dimensions = parsePng(buffer);
             } else if (isJpeg(buffer)) {
             const dims = await imageDimensionsFromStream(file.stream());
             if (dims === undefined) {
             throw Error("Couldn't get file dimensions");
             }
             dimensions = {
width: dims.width,
height: dims.height,
orientation: 0,
type: "jpeg",
};
*/
  /*
             const exifData = await window.exifr.parse(file, {
translateValues: false,
exif: ["Orientation", "DateTimeOriginal", "ImageWidth", "ImageHeight"],
});
let imageWidth = exifData.ImageWidth;
let imageHeight = exifData.ImageHeight;
let orientation = 0;
if (exifData.Orientation !== undefined) {
orientation = exifData.Orientation;
}
if (imageWidth === undefined || imageHeight === undefined) {
const dims = await imageDimensionsFromStream(file.stream());
if (dims === undefined) {
throw Error("Couldn't get file dimensions");
}
imageWidth = dims.width;
imageHeight = dims.height;
}

const orientationToInt: Record<number, number> = { 1: 0, 3: 2, 6: 1, 8: 3 };
const rotation = orientationToInt[orientation] ?? 0;
const flip = rotation % 2 !== 0;

dimensions = {
width: flip ? imageHeight : imageWidth,
height: flip ? imageWidth : imageHeight,
orientation: rotation,
type: "jpeg",
};
*/
  /*
             } else if (isGif(buffer)) {
             dimensions = parseGif(buffer);
             } else if (isWebp(buffer)) {
             dimensions = parseWebp(buffer);
             } else {
             throw new Error("Unsupported image format");
             }

             return [dimensions.width, dimensions.height, dimensions.orientation ?? 0];
             */
};

function isPng(buffer: Uint8Array): boolean {
  return (
    buffer[0] === 0x89 &&
    buffer[1] === 0x50 &&
    buffer[2] === 0x4e &&
    buffer[3] === 0x47
  );
}

function isJpeg(buffer: Uint8Array): boolean {
  return buffer[0] === 0xff && buffer[1] === 0xd8;
}

function isGif(buffer: Uint8Array): boolean {
  return buffer[0] === 0x47 && buffer[1] === 0x49 && buffer[2] === 0x46;
}

function isWebp(buffer: Uint8Array): boolean {
  return (
    buffer[0] === 0x52 &&
    buffer[1] === 0x49 &&
    buffer[2] === 0x46 &&
    buffer[3] === 0x46 &&
    buffer[8] === 0x57 &&
    buffer[9] === 0x45 &&
    buffer[10] === 0x42 &&
    buffer[11] === 0x50
  );
}

function parsePng(buffer: Uint8Array): PngDimensions {
  const width =
    (buffer[16] << 24) | (buffer[17] << 16) | (buffer[18] << 8) | buffer[19];
  const height =
    (buffer[20] << 24) | (buffer[21] << 16) | (buffer[22] << 8) | buffer[23];
  return { width, height, type: "png" };
}

function parseGif(buffer: Uint8Array): GifDimensions {
  const width = buffer[6] | (buffer[7] << 8);
  const height = buffer[8] | (buffer[9] << 8);
  return { width, height, type: "gif" };
}

function parseWebp(buffer: Uint8Array): WebpDimensions {
  const chunkHeader = String.fromCharCode(
    buffer[12],
    buffer[13],
    buffer[14],
    buffer[15]
  );
  if (chunkHeader === "VP8 ") {
    const width = buffer[26] | (buffer[27] << 8);
    const height = buffer[28] | (buffer[29] << 8);
    return { width, height, type: "webp" };
  } else if (chunkHeader === "VP8L") {
    const b0 = buffer[21];
    const b1 = buffer[22];
    const b2 = buffer[23];
    const b3 = buffer[24];
    const width = 1 + (((b1 & 0x3f) << 8) | b0);
    const height = 1 + (((b3 & 0xf) << 10) | (b2 << 2) | ((b1 & 0xc0) >> 6));
    return { width, height, type: "webp" };
  } else if (chunkHeader === "VP8X") {
    const width = 1 + (buffer[24] | (buffer[25] << 8) | (buffer[26] << 16));
    const height = 1 + (buffer[27] | (buffer[28] << 8) | (buffer[29] << 16));
    return { width, height, type: "webp" };
  } else {
    throw new Error("Unknown WebP format");
  }
}

function parseJpeg(buffer: Uint8Array): JpegDimensions {
  let i = 2;
  let orientation: number | null = null;

  while (i < buffer.length) {
    if (buffer[i] !== 0xff) {
      i++;
      continue;
    }
    const marker = buffer[i + 1];
    if (
      marker === 0xd8 ||
      marker === 0xd9 ||
      (marker >= 0xd0 && marker <= 0xd7)
    ) {
      i += 2;
      continue;
    }
    const length = (buffer[i + 2] << 8) + buffer[i + 3];
    if (marker === 0xe1) {
      const exifSection = buffer.subarray(i + 4, i + 2 + length);
      orientation = readExifOrientation(exifSection);
    } else if (marker >= 0xc0 && marker <= 0xcf && marker !== 0xcc) {
      const height = (buffer[i + 5] << 8) + buffer[i + 6];
      const width = (buffer[i + 7] << 8) + buffer[i + 8];
      return { width, height, type: "jpeg", orientation };
    }
    i += 2 + length;
  }
  throw new Error("Could not find JPEG size info");
}

function readExifOrientation(buff: Uint8Array): number | null {
  if (
    buff[0] !== 0x45 ||
    buff[1] !== 0x78 ||
    buff[2] !== 0x69 ||
    buff[3] !== 0x66
  )
    return null;
  const le = buff[6] === 0x49 && buff[7] === 0x49;
  let off = 10;
  const entries = readShort(buff, off, le);
  off += 2;
  for (let i = 0; i < entries; i++) {
    const tag = readShort(buff, off + 12 * i, le);
    if (tag === 0x0112) return readShort(buff, off + 12 * i + 8, le);
  }
  return null;
}

function readShort(buff: Uint8Array, off: number, le: boolean): number {
  return le
    ? buff[off] + (buff[off + 1] << 8)
    : (buff[off] << 8) + buff[off + 1];
}

function makeId(len: number): string {
  let res = "";
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  for (let i = 0; i < len; i++)
    res += chars.charAt(Math.floor(Math.random() * chars.length));
  return res;
}

function readFileAsUint8Array(file: File): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
}

async function* getFilesRecursively(
  path: string,
  entry: any
): AsyncGenerator<File> {
  if (entry.kind === "file") {
    const file = await entry.getFile();
    yield file;
  } else {
    for await (const handle of entry.values()) {
      yield* getFilesRecursively(path + entry.name + "/", handle);
    }
  }
}

window.pma_js_p_pick_images_has_run = false;
window.pma_js_pick_images = async () => {
  if (window.pma_js_p_pick_images_has_run) return;
  if ((window as any).pma_js_testing === true) {
    allTestingImages = (await (await fetch("/test_images.json")).json()).imgs;
    allAlbums.length = 0;
    allAlbums.push({
      id: testingRootPath,
      name: testingRootPath,
      count: 0,
      images: [],
    });
    for (const img of allTestingImages) {
      allAlbums[0].images.push({
        path: testingRootPath + img.file,
        size: [img.width, img.height, 0],
      });
      allAlbums[0].count++;
    }
    window.pmaFlSmartphoneSelectionServiceUpdateAlbums(allAlbums);
    return;
  }
  window.pma_js_p_pick_images_has_run = true;
  const dir = await (window as any).showDirectoryPicker();
  for await (const fh of getFilesRecursively("", dir)) {
    allImages.push(fh);
  }
  for (const image of allImages) {
    const idx = allAlbums.findIndex((a) => a.id === image.webkitRelativePath);
    const path = image.webkitRelativePath + image.name;
    console.log("ONE call");
    const info: ImageInfo = {
      path,
      size: await window.pma_js_image_dimensions(path),
    };
    console.log("ONE call END");
    if (idx === -1) {
      allAlbums.push({
        id: image.webkitRelativePath,
        name: image.webkitRelativePath,
        count: 1,
        images: [info],
      });
    } else {
      allAlbums[idx].count++;
      allAlbums[idx].images.push(info);
    }
  }
  window.pmaFlSmartphoneSelectionServiceUpdateAlbums(allAlbums);
};

window.pma_js_get_images = () =>
  allAlbums.flatMap((a) => a.images.map((i) => i.path));
window.pma_js_get_albums = () => allAlbums;

window.pma_js_get_image_data = (photoId: string) => {
  if ((window as any).pma_js_testing === true) {
    return fetch(`/${photoId}`).then((response) =>
      response.arrayBuffer().then((buf) => new Uint8Array(buf))
    );
  }
  const img = allImages.find(
    (it) => it.webkitRelativePath + it.name === photoId
  );
  if (!img) throw new Error(`Couldn't load image ${photoId}`);
  return readFileAsUint8Array(img);
};

window.pma_js_p_tmp_images = [];

async function resizeAndSaveToJpegBytes(
  imgEl: HTMLImageElement,
  arg: ResizeImageArg
): Promise<Uint8Array> {
  const c = document.createElement("canvas");
  c.width = arg.out_size.width;
  c.height = arg.out_size.height;
  const ctx = c.getContext("2d");
  if (!ctx) throw Error("");
  console.log("RESIZING TO: ", arg);
  const isRotated = arg.angle % 2 !== 0;
  var dstX = 0;
  var dstY = 0;
  var dstW = arg.out_size.width;
  var dstH = arg.out_size.height;
  if (arg.angle !== 0) {
    console.log("jak-dbg is rotated");
    ctx.save();
    // move to the center of the canvas
    ctx.translate(c.width / 2, c.height / 2);
    if (isRotated) {
      dstX = -c.height / 2;
      dstY = -c.width / 2;
      dstW = arg.out_size.height;
      dstH = arg.out_size.width;
    } else {
      dstX = -c.width / 2;
      dstY = -c.height / 2;
    }
    // rotate the canvas to the specified degrees
    ctx.rotate((arg.angle * Math.PI) / 2);
    console.log("jak-dbg is not");
  }
  ctx.drawImage(
    imgEl,
    arg.crop.left,
    arg.crop.top,
    arg.crop.width,
    arg.crop.height,
    dstX,
    dstY,
    dstW,
    dstH
  );
  if (isRotated) {
    ctx.restore();
  }
  const blob = (await new Promise<Blob | null>((res) =>
    c.toBlob(res, "image/jpeg", 0.95)
  ))!;
  return new Uint8Array(await blob.arrayBuffer());
}

async function fileToImage(file: File): Promise<HTMLImageElement> {
  const url = URL.createObjectURL(file);
  const img = new Image();
  return new Promise((res, rej) => {
    img.onload = () => {
      URL.revokeObjectURL(url);
      res(img);
    };
    img.onerror = rej;
    img.src = url;
  });
}

async function webUrlToImage(webUrl: string): Promise<HTMLImageElement> {
  const img = new Image();
  return new Promise((res, rej) => {
    img.onload = () => {
      res(img);
    };
    img.onerror = rej;
    img.src = webUrl;
  });
}

async function resizeFileToJpegBytes(
  file: File,
  arg: ResizeImageArg
): Promise<Uint8Array> {
  return resizeAndSaveToJpegBytes(await fileToImage(file), arg);
}

async function resizeWebUrlToJpegBytes(
  webUrl: string,
  arg: ResizeImageArg
): Promise<Uint8Array> {
  return resizeAndSaveToJpegBytes(await webUrlToImage(webUrl), arg);
}

window.pma_js_resize_image = async (jsonArg: string) => {
  const arg = JSON.parse(jsonArg) as ResizeImageArg;
  console.log(JSON.parse(jsonArg));
  let bytes: Uint8Array;
  if ((window as any).pma_js_testing === true) {
    bytes = await resizeWebUrlToJpegBytes(`/${arg.in_filename}`, arg);
  } else {
    const imgFile = allImages.find(
      (it) => it.webkitRelativePath + it.name === arg.in_filename
    );
    if (!imgFile) throw new Error(`Couldn't resize image ${arg.in_filename}`);
    bytes = await resizeFileToJpegBytes(imgFile, arg);
  }
  let key: string | null = null;
  for (let i = 0; i < 10; i++) {
    const k = makeId(10);
    if (!window.pma_js_p_tmp_images.find((e) => e.key === k)) {
      key = k;
      break;
    }
  }
  if (!key) throw new Error("Too many keys");
  window.pma_js_p_tmp_images.push({ key, bytes });
  return key;
};

window.pma_js_resized_image_bytes = (tmpKey: string) => {
  const ent = window.pma_js_p_tmp_images.find((e) => e.key === tmpKey);
  if (!ent) throw new Error(`Couldn't retrieve resized image ${tmpKey}`);
  return ent.bytes;
};

window.pma_js_add_folder = async () => {
  // const dir = await (window as any).showDirectoryPicker();
  const dir = await showDirectoryPicker();
  const result: string[] = [];
  for await (const fh of getFilesRecursively("", dir)) {
    allImages.push(fh);
    result.push(fh.webkitRelativePath + fh.name);
  }
  return result;
};

window.pma_js_add_files = async () => {
  // const files = await (window as any).showOpenFilePicker({
  const files = await showOpenFilePicker({
    multiple: true,
    types: [
      {
        description: "Image files",
        accept: {
          "image/*": [".png", ".jpg", ".jpeg", ".gif", ".bmp", ".webp", ".svg"],
        },
      },
    ],
    excludeAcceptAllOption: false,
  });
  const result: string[] = [];
  for (const fh of files) {
    const file = await fh.getFile();
    allImages.push(file);
    result.push(file.webkitRelativePath + file.name);
  }
  return result;
};

document.addEventListener("dragenter", (event) => {
  console.log("dragenter called");
  if (dropIsActive) {
    console.log("set hovering true called.");
    window.pmaFlPersonalizePageSetHovering(true);
  }
});

document.addEventListener("dragleave", (event) => {
  console.log("dragleave called");
  if (dropIsActive) {
    console.log("set hovering called: false.");
    window.pmaFlPersonalizePageSetHovering(false);
  }
});

document.addEventListener("dragover", (event) => {
  // prevent default to allow drop
  event.preventDefault();
});

document.addEventListener("drop", (event) => {
  // prevent default action (open as a link for some elements)
  event.preventDefault();
  console.log(event);
  if (dropIsActive) {
    const photosToAdd: string[] = [];
    const dataTransfer = event.dataTransfer;
    if (dataTransfer !== null) {
      if (dataTransfer.items) {
        // Use DataTransferItemList interface to access the file(s)
        [...dataTransfer.items].forEach((item, i) => {
          // If dropped items aren't files, reject them
          if (item.kind === "file") {
            const file = item.getAsFile();
            if (file !== null) {
              console.log(`… file[${i}].name = ${file.name}`);
              allImages.push(file);
              photosToAdd.push(file.webkitRelativePath + file.name);
            }
          }
        });
      } else {
        // Use DataTransfer interface to access the file(s)
        [...dataTransfer.files].forEach((file, i) => {
          console.log(`… file[${i}].name = ${file.name}`);
          allImages.push(file);
          photosToAdd.push(file.webkitRelativePath + file.name);
        });
      }
    }
    if (photosToAdd.length > 0) {
      window.pmaFlPersonalizePageAddPhotos(photosToAdd);
    }
  }
});

window.pma_js_set_drop_active = (newValue) => {
  dropIsActive = newValue;
};

window.pma_js_get_default_language = () => {
  if (window.location.hostname.substr(3, 18) == "web.printeeapp.com") {
    return window.location.hostname.substr(0, 2);
  } else {
    return "en";
  }
};

export {};
